import {BaseModel} from '@/models/base.model';
import {Action, Mutation, VuexModule} from 'vuex-module-decorators';
import {PaginatedResponse} from '@/api/base.api';
import {compare} from '@/utils/utils';

export abstract class EntityStateAdapter<T extends BaseModel> extends VuexModule {
  protected _entities: T[] = [];
  protected _loading: boolean = false;
  protected _currentPage: number = 1;
  protected _lastPage: number = 1;
  protected _total: number = 0;
  protected _perPage: number = 0;

  /**
   * Getters bellow
   */

  get loading(): boolean {
    return this._loading;
  }

  get currentPage(): number {
    return this._currentPage;
  }

  get lastPage(): number {
    return this._lastPage;
  }

  get total(): number {
    return this._total;
  }

  get perPage(): number {
    return this._perPage;
  }

  get entities(): T[] {
    const state = this._entities;
    return state.sort((a, b) => compare(a.id, b.id, true))
  }

  get selectEntityOrderByCreatedAt(): T[] {
    const state = this._entities;
    return state.sort((a, b) => compare(a.created_at, b.created_at, true))
  }

  get selectEntityOrderByUpdatedAt(): T[] {
    const state = this._entities;
    return state.sort((a, b) => compare(a.updated_at, b.updated_at, true))
  }

  /**
   * Muatations below
   */
  @Mutation
  updateAllPageParams(params: PaginatedResponse<T>): void {
    this._perPage = params?.per_page;
    this._total = params?.total;
    this._lastPage = params?.last_page;
    this._currentPage = params?.current_page;
  }

  @Mutation
  updateTotal(total: number): void {
    this._total = total
  }

  @Mutation
  updatePerPage(perPage: number): void {
    this._perPage = perPage
  }

  @Mutation
  updateLastPage(page: number): void {
    this._lastPage = page
  }

  @Mutation
  updateCurrentPage(page: number): void {
    this._currentPage = page;
  }

  @Mutation
  upsertOne(entity: T): void {
    if (entity.id) {
      const index = this._entities.findIndex((v) => v.id === entity.id);
      if (~index) {
        this._entities.splice(index, 1, entity)
      } else {
        this._entities.push(entity)
      }
    } else {
      entityWithoutIdWarn()
    }
    this._loading = false;
  }

  @Mutation
  insertOne(entity: T): void {
    this._entities.push(entity);
    this._loading = false;
  }

  @Mutation
  addMany(entities: T[]): void {
    this._entities.push(...entities);
    this._loading = false;
  }

  @Mutation
  upsertMany(entities: T[]): void {
    entities.forEach((v) => {
      const index = this._entities.findIndex((i) => i.id === v.id);
      if (~index) {
        this._entities.splice(index, 1, v)
      } else {
        this._entities.push(v)
      }
    });
    this._loading = false;
  }

  @Mutation
  insertMany(entities: T[]): void {
    this._entities = entities;
    this._loading = false;
  }

  @Mutation
  removeOne(id: number): void {
    const index = this._entities.findIndex((v) => v.id === id);
    if (~index) {
      this._entities.splice(index, 1)
    }
    this._loading = false;
  }

  @Mutation
  loadingUpdated(status: boolean): void {
    this._loading = status
  }

  /**
   * Actions below
   */

  @Action({commit: 'loadingUpdated', rawError: true})
  updateLoading(status: boolean) {
    return status;
  }

  @Action({commit: 'updateAllPageParams', rawError: true})
  updatePageParams(pageParams: PaginatedResponse<T>) {
    return pageParams;
  }

}

const entityWithoutIdWarn = (): void => console.warn('entity must to have a ID');
